import { ExtendedAccommodationCatalogPropertyFragment, TravelJoyPlacePricingAndBookingDetailsFragment, TravelPlacePricingAndBookingDetailsFragment } from '@graphql/generated';
import { LocaleContextType } from '@shared/core/i18n/LocaleContext';
import { addError } from '@shared/utils/logger';

export const translatedPrice = (priceInMinorUnits: number, digits: number, currency: string, locale: NavigatorLanguage['language'] = 'en-US', showDecimals: boolean = false) => {
  return (priceInMinorUnits / Math.pow(10, digits)).toLocaleString(locale, {
    style: 'currency',
    ...(showDecimals ? { maximumFractionDigits: digits, minimumFractionDigits: digits } : { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
    currency,
  });
};
export const getPercentageOff = (
  minimumPercentageOffToDisplay: number,
  priceInMinorUnits?: number,
  strikeOutPriceInMinorUnits?: number,
  priceDigits?: number,
  strikeOutPriceDigits?: number,
): number | undefined => {
  if (!priceInMinorUnits || !strikeOutPriceInMinorUnits || !priceDigits || !strikeOutPriceDigits) return undefined;

  const price = priceInMinorUnits / Math.pow(10, priceDigits);
  const strikeOutPrice = strikeOutPriceInMinorUnits / Math.pow(10, strikeOutPriceDigits);
  const percentageOff = Math.round(((strikeOutPrice - price) / strikeOutPrice) * 100);
  return percentageOff >= minimumPercentageOffToDisplay ? percentageOff : undefined;
};

export const getCalculatedPrice = (
  priceDataFromApi: TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment | ExtendedAccommodationCatalogPropertyFragment | null,
  locale: LocaleContextType,
  minimumPercentageOffToDisplay: number,
  travelPlaceId?: string | null,
  complaintRef?: React.MutableRefObject<boolean>,
) => {
  const priceData = priceDataFromApi?.pricingInfo?.nightlyBaseRate;
  const minorUnits = priceData?.valueInMinorUnits;
  const minorUnitsDigits = priceData?.digits;
  const currency = priceData?.currency;
  const price = (travelPlaceId && (minorUnits && currency ? translatedPrice(minorUnits, minorUnitsDigits!, currency, locale.language) : null)) || undefined;
  const strikeOutPriceData = priceDataFromApi?.pricingInfo?.strikeoutNightlyBaseRate;
  const strikeOutMinorUnits = strikeOutPriceData?.valueInMinorUnits;
  const strikeOutMinorUnitsDigits = strikeOutPriceData?.digits;
  const strikeOutCurrency = strikeOutPriceData?.currency;
  const strikeOutPriceValid = strikeOutMinorUnits && minorUnits && strikeOutMinorUnits > minorUnits;

  if (strikeOutMinorUnits && !strikeOutPriceValid && !complaintRef?.current) {
    if (complaintRef) {
      complaintRef.current = true;
    }
    addError(new Error('Strikeout price is not valid'), {
      context: 'useTravelPlacesPricingAndBookingDetailsFragment',
      strikeOutPriceUnits: strikeOutMinorUnits,
      priceUnits: minorUnits,
      travelPlaceId: travelPlaceId,
    });
  }
  const strikeOutPrice =
    strikeOutPriceValid && strikeOutMinorUnits && strikeOutCurrency
      ? translatedPrice(strikeOutMinorUnits, strikeOutMinorUnitsDigits!, strikeOutCurrency, locale.language)
      : undefined;

  const percentageOff = getPercentageOff(minimumPercentageOffToDisplay, minorUnits, strikeOutMinorUnits, minorUnitsDigits, strikeOutMinorUnitsDigits);

  const totalPriceData = priceDataFromApi?.pricingInfo?.totalRate;
  const totalMinorUnits = totalPriceData?.valueInMinorUnits;
  const totalMinorUnitsDigits = totalPriceData?.digits ?? 2;
  const totalCurrency = totalPriceData?.currency;
  const totalAmount =
    (travelPlaceId && (totalMinorUnits && totalCurrency ? translatedPrice(totalMinorUnits, totalMinorUnitsDigits, totalCurrency, locale.language) : null)) || undefined;

  return { price, strikeOutPrice, percentageOff, minorUnits, totalAmount };
};
